import map from 'lodash/map';
import * as actions from 'actions/scriptStepModal';
import { librariesArrayToMap } from 'pages/ScriptStepModal/libraryReference';
import {
  defaultCallableTriggerIconPath,
  defaultStepIconPath,
  defaultTriggerIconPath,
  INPUT_MAX_LENGTH,
  scriptStepTypes,
  ScriptStepStageDefs,
} from '@xmatters/common/constants/scriptSteps';

import { defaultExample, customHttpTriggerExample } from './scriptExamples';
import { unpackPagedData } from '../utils';

const helpId = 'FlowScriptReference'; // CSHID for custom step script guide

const getDefaultData = () => ({
  name: '',
  description: '',
  icon: defaultStepIconPath,
  inputs: [],
  outputs: [],
  libraries: {}, // Transformed from array to map for the UI
  allowedEnvironment: 'HOSTED',
  authenticationMethod: 'URL_AUTH',
  scriptStepType: scriptStepTypes.USER,
  stage: ScriptStepStageDefs.DEVELOPMENT,
  withdrawnDate: null,
  scriptCode: defaultExample,
});

const defaultDataOverridesByStepType = {
  [scriptStepTypes.CALLABLE_FLOW_CALLER]: {
    icon: defaultCallableTriggerIconPath,
    scriptCode: '1;',
    scriptStepType: scriptStepTypes.CALLABLE_FLOW_CALLER,
    stage: ScriptStepStageDefs.DEPLOYED,
    outputs: [
      {
        name: 'Request ID',
        description:
          'The unique identifier (UUID) of the request that initiated the trigger.',
        position: 0,
      },
    ],
  },
  [scriptStepTypes.CUSTOM_HTTP]: {
    icon: defaultTriggerIconPath,
    inputs: undefined,
    scriptCode: customHttpTriggerExample,
    scriptStepType: scriptStepTypes.CUSTOM_HTTP,
  },
};

function unpackPagedInputsOutputs(payload) {
  return {
    ...payload,
    inputs: unpackPagedData(payload.inputs),
    outputs: unpackPagedData(payload.outputs),
  };
}

// "ANY_AUTH" type is treated as `null` in xm-api.
// Use endpointLabel as the source of truth since it is a required field when enabling endpoints:
function hasEndpoint(stepData) {
  return stepData !== undefined && !!stepData.endpointLabel;
}

function dataWithPlan(data, commPlanUUID) {
  if (commPlanUUID) {
    return {
      ...data,
      plan: { id: commPlanUUID },
    };
  }
  return data;
}

function buildData(stepData, commPlanUUID) {
  const baseData = stepData
    ? unpackPagedInputsOutputs(stepData)
    : getDefaultData();
  const data = dataWithPlan(baseData, commPlanUUID);

  if (hasEndpoint(stepData)) {
    data.includeEndpoint = true;
    if (data.endpointType === undefined) {
      data.endpointType = 'ANY_AUTH';
    }
  }

  if (Array.isArray(data.libraries)) {
    data.libraries = librariesArrayToMap(data.libraries);
  }

  return data;
}

const getNewStepDefaultData = ({
  scriptStepType,
  commPlanUUID,
  dataOverrides,
}) => {
  const baseData = {
    ...getDefaultData(),
    ...(defaultDataOverridesByStepType[scriptStepType] || {}),
    ...dataOverrides,
  };
  return dataWithPlan(baseData, commPlanUUID);
};

function setDevelopmentVersion(step, { version } = { version: undefined }) {
  const copy = {
    ...step,
    // set the new version to DEVELOPMENT
    stage: 'DEVELOPMENT',
    versionLabel: '',
    versionInfo: '',
  };

  if (version !== undefined) {
    copy.version = version;
  }

  return copy;
}

export const defaultState = {
  data: getDefaultData(),
  show: false,
  submitting: false,
  error: undefined,
  showEndpointSettings: false,
  showVersionSelectionAlert: true,
  usage: [],
  isVersionsExpanded: false,
};

const cleanupForCopyInputsOrOutputs = entryList =>
  entryList.map(e => {
    // id, scriptStep are pulled out and discarded
    const { id, scriptStep, ...copy } = e;
    return copy;
  });

const cleanupForCopy = stepData => {
  // fields are pulled out and discarded
  const {
    id,
    uuid,
    owner,
    versionCreatedAt,
    versionCreator,
    accessibleByAll,
    permitted,
    version,
    versions,
    usage,
    ...copy
  } = stepData;

  copy.inputs = cleanupForCopyInputsOrOutputs(copy.inputs);
  copy.outputs = cleanupForCopyInputsOrOutputs(copy.outputs);

  return copy;
};

function handleStepSaveSuccess(
  state,
  scriptStep,
  { commPlanUUID, isNewVersion },
) {
  const { usage, ...data } = buildData(scriptStep, commPlanUUID);

  return {
    ...state,
    helpId,
    isNew: false,
    isNewVersion: !!isNewVersion,
    isNewCopy: false,
    show: true,
    supportedScriptVersion: window.XM_getSupportedScriptVersion(),
    data,
    showEndpointSettings: data.includeEndpoint,
    usage,
    scriptStepType: scriptStep.scriptStepType,
  };
}

export default function scriptStepModal(state = defaultState, action) {
  const { payload } = action;

  switch (action.type) {
    case actions.SCRIPT_STEP_MODAL_LOAD_START: {
      return {
        ...state,
        selectedTab: payload.selectedTab || null,
        isVersionsExpanded: payload.isVersionsExpanded || false,
      };
    }
    case actions.SCRIPT_STEP_MODAL_SAVE_SUCCESS: {
      const { usage } = action.payload;
      const scriptStep = {
        ...action.payload,
        usage: usage.length > 0 ? usage : state.usage,
      };
      return handleStepSaveSuccess(state, scriptStep, {
        commPlanUUID: action.commPlanUUID,
        isNewVersion: false,
      });
    }
    case actions.SCRIPT_STEP_MODAL_LOAD_SUCCESS: {
      return handleStepSaveSuccess(state, action.payload, {
        commPlanUUID: action.commPlanUUID,
        isNewVersion: !payload.id,
      });
    }
    case actions.SCRIPT_STEP_MODAL_NEW: {
      return {
        ...state,
        helpId,
        isNew: true,
        show: true,
        supportedScriptVersion: window.XM_getSupportedScriptVersion(),
        data: getNewStepDefaultData(action.payload),
        showEndpointSettings: false,
        scriptStepType: action.payload.scriptStepType,
      };
    }
    case actions.SCRIPT_STEP_MODAL_COPY_STEP: {
      const { baseStep, namePrefix, commPlanUUID } = action;

      // This convoluted name means this functions figures out whether
      // a step should be copied as a custom HTTP trigger or a custom script step
      const pickCopyScriptStepTypeByBuiltInBase = () =>
        baseStep.scriptStepType === scriptStepTypes.BUILT_IN_HTTP
          ? scriptStepTypes.CUSTOM_HTTP
          : scriptStepTypes.USER;

      let copy = cleanupForCopy({
        ...buildData(baseStep, commPlanUUID),
        icon: baseStep.icon || defaultStepIconPath,
        isBuiltIn: false,
        name: `${namePrefix} ${baseStep.name}`,
        scriptStepType: baseStep.isBuiltIn
          ? pickCopyScriptStepTypeByBuiltInBase()
          : baseStep.scriptStepType,
      });

      copy.inputs = map(copy.inputs, input => ({
        ...input,
        maxLength:
          input.maxLength && Math.min(input.maxLength, INPUT_MAX_LENGTH),
      }));

      copy = setDevelopmentVersion(copy, { version: 1 });

      delete copy.rootStep;

      // we need to remove the id's of references from the previous step
      // otherwise these references will all move to the new step
      Object.keys(copy.libraries || {}).forEach(key => {
        delete copy.libraries[key].id;
      });

      return {
        ...state,
        data: copy,
        helpId,
        isNew: true,
        isNewCopy: true,
        show: true,
        showEndpointSettings: copy.includeEndpoint,
        supportedScriptVersion: window.XM_getSupportedScriptVersion(),
      };
    }
    case actions.SCRIPT_STEP_MODAL_NEW_VERSION: {
      const { baseStep, copyString } = action;
      const root = baseStep.version === 1 ? baseStep.id : baseStep.rootStep.id;
      let newStepVersion = cleanupForCopy({
        ...buildData(baseStep),
        name:
          baseStep.id === 'blank'
            ? baseStep.name
            : `${copyString} ${baseStep.name}`,
        rootStep: { id: root },
      });

      newStepVersion = setDevelopmentVersion(newStepVersion);

      // we need to remove the id's of references from the previous step
      // otherwise these references will all move to the new step
      Object.keys(newStepVersion.libraries || {}).forEach(key => {
        delete newStepVersion.libraries[key].id;
      });

      return {
        ...state,
        data: newStepVersion,
        helpId,
        isNewVersion: true,
        show: true,
        showEndpointSettings: newStepVersion.includeEndpoint,
        supportedScriptVersion: window.XM_getSupportedScriptVersion(),
        usage: [],
      };
    }
    case actions.SCRIPT_STEP_MODAL_HIDE:
      // Clear the state once we close the modal. Otherwise, race conditions would happen for usages.
      return defaultState;
    case actions.SCRIPT_STEP_MODAL_TOGGLE_ENDPOINT:
      return {
        ...state,
        showEndpointSettings: !state.showEndpointSettings,
      };
    case actions.SCRIPT_STEP_VERSION_SELECTION_ALERT_DISMISSED:
      return {
        ...state,
        showVersionSelectionAlert: false,
      };
    case actions.SCRIPT_STEP_MODAL_SET_VERSION_EXPANDED:
      return {
        ...state,
        isVersionsExpanded: payload.isVersionsExpanded,
      };
    default:
      return state;
  }
}
